import { memo } from "react";
import type { IntelligenceItem, FallbackTier, NewsDigestTopline } from "@chula/shared";
import { uiStrings } from "@chula/shared";
import { safeUrl } from "../lib/safeUrl";
import { langOf } from "../lib/lang";
import { PanelHeader } from "./PanelHeader";

function ago(iso: string): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "—";
  const ms = Date.now() - t;
  const m = Math.round(ms / 60000);
  if (m < 1) return "NOW";
  if (m < 60) return `${m}M`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}H`;
  return `${Math.round(h / 24)}D`;
}

interface Props {
  items: IntelligenceItem[];
  loading: boolean;
  ageMinutes?: number;
  fallbackTier?: FallbackTier;
  onRefresh?: () => void;
  /** Set to true (via useFeed fallbackTier === "unavailable") to show error state. */
  unavailable?: boolean;
  locale?: "en" | "th" | "zh";
  /** Top headlines from /api/news/digest — highest-scored items over the last 7 days. */
  digestToplines?: NewsDigestTopline[];
  /** Total articles archived in the digest window (for the eyebrow label). */
  digestTotal?: number;
}

export const NewsDesk = memo(function NewsDesk({ items, loading, ageMinutes, fallbackTier, onRefresh, unavailable, locale = "en", digestToplines, digestTotal }: Props) {
  const s = uiStrings(locale);
  const title = locale === "th" ? "ข่าวมหาวิทยาลัย" : locale === "zh" ? "校园新闻" : "CAMPUS NEWS";

  if (loading && items.length === 0) {
    return (
      <div className="col">
        <PanelHeader title={title} source="rss·google-news" fallbackTier={null} />
        <div className="skeleton" style={{ height: 10, marginTop: 8 }} />
        <div className="skeleton" style={{ height: 10, width: "80%", marginTop: 6 }} />
        <div className="skeleton" style={{ height: 10, width: "60%", marginTop: 6 }} />
      </div>
    );
  }

  if (unavailable && items.length === 0) {
    return (
      <div className="col">
        <PanelHeader title={title} source="rss·google-news" fallbackTier="unavailable" />
        <div className="caption" style={{ color: "var(--text-3)", marginTop: 8 }}>
          {s.unavailable}.
        </div>
      </div>
    );
  }

  return (
    <div className="col">
      <PanelHeader
        title={title}
        ageMinutes={ageMinutes}
        fallbackTier={fallbackTier ?? null}
        source="rss·google-news"
        actions={
          <span className="news-count-cluster">
            <span className="news-count mono">{String(items.length).padStart(3, "0")}</span>
            {onRefresh && (
              <button
                type="button"
                className="news-refresh mono"
                onClick={onRefresh}
                disabled={loading}
                title={ageMinutes != null ? `Refreshed ${ageMinutes}m ago — click to refresh` : "Refresh"}
                aria-label="Refresh news"
              >
                {loading ? "…" : "↻"}
              </button>
            )}
          </span>
        }
      />
      {digestToplines && digestToplines.length > 0 && (
        <div className="news-digest-block">
          <div className="eyebrow mono news-digest-eyebrow">
            THIS WEEK
            {digestTotal != null && digestTotal > 0 && (
              <span className="caption" style={{ color: "var(--text-3)" }}> · {digestTotal} articles</span>
            )}
          </div>
          {digestToplines.map((dl) => {
            const url = safeUrl(dl.url);
            const body = (
              <>
                <div className="title" lang={langOf(dl.title)}>{dl.title}</div>
                <div className="meta">
                  <span>{dl.source.toUpperCase()}</span>
                  <span>·</span>
                  <span className="mono">{ago(dl.firstSeenAt)}</span>
                </div>
              </>
            );
            // No valid URL → plain block, not a dead "#" link.
            return url ? (
              <a key={dl.url} href={url} target="_blank" rel="noreferrer noopener" className="news-item news-item--digest">
                {body}
              </a>
            ) : (
              <span key={dl.url} className="news-item news-item--digest">{body}</span>
            );
          })}
          <div className="news-digest-sep" />
        </div>
      )}
      {items.length === 0 && (
        <div className="caption" style={{ color: "var(--text-3)", marginTop: 8 }}>No recent news.</div>
      )}
      <div>
        {items.slice(0, 12).map((it) => {
          const url = safeUrl(it.sourceUrl);
          const body = (
            <>
              <div className="title" lang={langOf(it.title)}>{it.title}</div>
              <div className="meta">
                <span>{it.source.toUpperCase()}</span>
                <span>·</span>
                <span className="mono">{ago(it.publishedAt)}</span>
              </div>
            </>
          );
          // No valid URL → plain block, not a dead "#" link.
          return url ? (
            <a key={it.id} href={url} target="_blank" rel="noreferrer noopener" className="news-item">
              {body}
            </a>
          ) : (
            <span key={it.id} className="news-item">{body}</span>
          );
        })}
      </div>
    </div>
  );
});
